import { Directive, ElementRef, EventEmitter, HostListener, Input, Output, NgModule } from '@angular/core';
import { BehaviorSubject, forkJoin, Subject } from 'rxjs';
import { debounceTime, filter, takeUntil, tap } from 'rxjs/operators';

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
/** @type {?} */
import * as ɵngcc0 from '@angular/core';
const TRAILING_WHITESPACE_AND_PUNCTUATION_REGEX = /[ .,;!?'‘’“”\-–—]+$/;
/** @type {?} */
const STYLE = 'overflow:hidden;overflow-wrap:break-word;word-wrap:break-word';
/** @type {?} */
const WHITE_SPACE = ' ';
class NgxLineClampDirective {
    /**
     * @param {?} el
     */
    constructor(el) {
        this.el = el;
        this.ellipsis = '\u2026';
        this.textTruncated = new EventEmitter();
        this.lineClamp$ = new BehaviorSubject(null);
        this.destroyed$ = new Subject();
    }
    /**
     * @param {?} parentElement
     * @return {?}
     */
    set parentElement(parentElement) {
        this._parentElement = parentElement;
    }
    /**
     * @return {?}
     */
    get parentElement() {
        return this._parentElement;
    }
    /**
     * @private
     * @return {?}
     */
    get lineClampAction$() {
        return this.lineClamp$.pipe(filter((/**
         * @param {?} v
         * @return {?}
         */
        v => !!v)), debounceTime(0), tap((/**
         * @return {?}
         */
        () => {
            this.setElementVariables();
            this.setStyle(this.rootElement, STYLE);
            /** @type {?} */
            const limitedMaxHeight = this.getLimitedMaxHeight(this.rootElement, this.parentElement);
            /** @type {?} */
            const textNode = document.createTextNode('');
            this.removeChildNodes(this.rootElement);
            this.rootElement.appendChild(textNode);
            /** @type {?} */
            const isTruncateText = this.truncateTextNode(this.text, textNode, limitedMaxHeight);
            if (isTruncateText) {
                this.textTruncated.emit(textNode.textContent);
                this.makeEllipsisInTrailing(textNode, limitedMaxHeight);
            }
        })));
    }
    /**
     * @return {?}
     */
    ngOnInit() {
        forkJoin([
            this.lineClampAction$
        ]).pipe(takeUntil(this.destroyed$)).subscribe();
    }
    /**
     * @param {?} changes
     * @return {?}
     */
    ngOnChanges(changes) {
        this.runLineClamp();
    }
    /**
     * @return {?}
     */
    ngAfterViewInit() {
        this.runLineClamp();
    }
    /**
     * @return {?}
     */
    ngOnDestroy() {
        this.destroyed$.next();
    }
    /**
     * @return {?}
     */
    scrollHandler() {
        this.runLineClamp();
    }
    /**
     * @return {?}
     */
    runLineClamp() {
        this.lineClamp$.next(true);
    }
    /**
     * @private
     * @return {?}
     */
    setElementVariables() {
        this.rootElement = this.el.nativeElement;
        if (!this.parentElement) {
            this.parentElement = (/** @type {?} */ (this.rootElement.parentNode));
        }
    }
    /**
     * @private
     * @param {?} element
     * @param {?} style
     * @return {?}
     */
    setStyle(element, style) {
        element.style.cssText += style;
    }
    /**
     * @private
     * @param {?} element
     * @param {?} property
     * @return {?}
     */
    getComputedStyle(element, property) {
        return window.getComputedStyle(element)[property];
    }
    /**
     * @private
     * @param {?} element
     * @param {?} property
     * @return {?}
     */
    getComputedStyleToPx(element, property) {
        /** @type {?} */
        const propertyStyle = this.getComputedStyle(element, property);
        return parseInt(propertyStyle.split('px')[0], 10);
    }
    /**
     * @private
     * @param {?} element
     * @return {?}
     */
    extractPureElementHeight(element) {
        /** @type {?} */
        const boxSizing = this.getComputedStyle(element, 'boxSizing');
        /** @type {?} */
        let height = this.getComputedStyleToPx(element, 'height');
        if (boxSizing === 'padding-box') {
            /** @type {?} */
            const paddingTop = this.getComputedStyleToPx(element, 'paddingTop');
            /** @type {?} */
            const paddingBottom = this.getComputedStyleToPx(element, 'paddingBottom');
            height = height - paddingTop - paddingBottom;
        }
        else if (boxSizing === 'border-box') {
            /** @type {?} */
            const paddingTop = this.getComputedStyleToPx(element, 'paddingTop');
            /** @type {?} */
            const paddingBottom = this.getComputedStyleToPx(element, 'paddingBottom');
            /** @type {?} */
            const borderTop = this.getComputedStyleToPx(element, 'borderTop');
            /** @type {?} */
            const borderBottom = this.getComputedStyleToPx(element, 'borderBottom');
            height = height - paddingTop - paddingBottom - borderTop - borderBottom;
        }
        return height;
    }
    /**
     * @private
     * @param {?} element
     * @param {?} parentElementHeight
     * @return {?}
     */
    calculateElementMaxHeight(element, parentElementHeight) {
        /** @type {?} */
        let lineHeight = this.getComputedStyle(element, 'lineHeight');
        if (lineHeight === 'normal') {
            /** @type {?} */
            const normal = 1.51;
            lineHeight = (this.getComputedStyleToPx(element, 'fontSize') * normal) + '';
        }
        else {
            lineHeight = this.getComputedStyleToPx(element, 'lineHeight') + '';
        }
        /** @type {?} */
        const maxLine = Math.floor(parentElementHeight / parseInt(lineHeight, 10));
        /** @type {?} */
        const adder = 1;
        return (this.lineCount || maxLine) * parseInt(lineHeight, 10) + adder;
    }
    /**
     * @private
     * @param {?} parentElement
     * @return {?}
     */
    hasScrollInElement(parentElement) {
        return parentElement.scrollHeight > parentElement.offsetHeight;
    }
    /**
     * @private
     * @param {?} text
     * @param {?} textNode
     * @param {?} limitedMaxHeight
     * @return {?}
     */
    truncateTextNode(text, textNode, limitedMaxHeight) {
        /** @type {?} */
        const words = text.split(WHITE_SPACE);
        /** @type {?} */
        let newTextContent = '';
        /** @type {?} */
        let prevTextContent = '';
        /** @type {?} */
        let wordIndex = 0;
        /** @type {?} */
        let isContentFull = false;
        while (words.length !== 0) {
            if (wordIndex + 1 > words.length) {
                break;
            }
            wordIndex = wordIndex + 1;
            newTextContent = words.slice(0, wordIndex).join(WHITE_SPACE);
            textNode.textContent = newTextContent;
            /** @type {?} */
            const rootHeight = this.getComputedStyleToPx(this.rootElement, 'height');
            /** @type {?} */
            const isRootElementOverMaxHeight = rootHeight >= limitedMaxHeight;
            if (isRootElementOverMaxHeight || this.hasScrollInElement(this.parentElement)) {
                textNode.textContent = prevTextContent;
                isContentFull = true;
                break;
            }
            prevTextContent = newTextContent;
        }
        return isContentFull;
    }
    /**
     * @private
     * @param {?} element
     * @return {?}
     */
    removeChildNodes(element) {
        /** @type {?} */
        const childNodes = element.childNodes;
        for (let i = 0; i < childNodes.length; i++) {
            /** @type {?} */
            const node = childNodes[i];
            element.removeChild(node);
        }
    }
    /**
     * @private
     * @param {?} textNode
     * @param {?} limitedMaxHeight
     * @return {?}
     */
    makeEllipsisInTrailing(textNode, limitedMaxHeight) {
        /** @type {?} */
        let textContent = textNode.textContent;
        /** @type {?} */
        let length = textContent.length;
        /** @type {?} */
        let limitCount = 30;
        while (length > 1 && limitCount > 0) {
            // Trim off one trailing character and any trailing punctuation and whitespace.
            textContent = textContent
                .substring(0, length - 1)
                .replace(TRAILING_WHITESPACE_AND_PUNCTUATION_REGEX, '');
            length = textContent.length;
            textNode.textContent = textContent + this.ellipsis;
            /** @type {?} */
            const rootHeight = this.getComputedStyleToPx(this.rootElement, 'height');
            /** @type {?} */
            const isRootElementInMaxHeight = rootHeight <= limitedMaxHeight;
            if (isRootElementInMaxHeight && !this.hasScrollInElement(this.parentElement)) {
                break;
            }
            limitCount--;
        }
    }
    /**
     * @private
     * @param {?} rootElement
     * @param {?} parentElement
     * @return {?}
     */
    getLimitedMaxHeight(rootElement, parentElement) {
        /** @type {?} */
        const parentElHeight = this.extractPureElementHeight(parentElement);
        return this.calculateElementMaxHeight(rootElement, parentElHeight);
    }
}
NgxLineClampDirective.ɵfac = function NgxLineClampDirective_Factory(t) { return new (t || NgxLineClampDirective)(ɵngcc0.ɵɵdirectiveInject(ɵngcc0.ElementRef)); };
NgxLineClampDirective.ɵdir = ɵngcc0.ɵɵdefineDirective({ type: NgxLineClampDirective, selectors: [["", "ngxLineClamp", ""]], hostBindings: function NgxLineClampDirective_HostBindings(rf, ctx) { if (rf & 1) {
        ɵngcc0.ɵɵlistener("resize", function NgxLineClampDirective_resize_HostBindingHandler() { return ctx.scrollHandler(); }, false, ɵngcc0.ɵɵresolveWindow);
    } }, inputs: { ellipsis: "ellipsis", parentElement: "parentElement", lineCount: "lineCount", text: "text" }, outputs: { textTruncated: "textTruncated" }, features: [ɵngcc0.ɵɵNgOnChangesFeature] });
/** @nocollapse */
NgxLineClampDirective.ctorParameters = () => [
    { type: ElementRef }
];
NgxLineClampDirective.propDecorators = {
    lineCount: [{ type: Input }],
    text: [{ type: Input }],
    ellipsis: [{ type: Input }],
    parentElement: [{ type: Input }],
    textTruncated: [{ type: Output }],
    scrollHandler: [{ type: HostListener, args: ['window:resize',] }]
};
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(NgxLineClampDirective, [{
        type: Directive,
        args: [{
                // tslint:disable-next-line:directive-selector
                selector: '[ngxLineClamp]'
            }]
    }], function () { return [{ type: ɵngcc0.ElementRef }]; }, { ellipsis: [{
            type: Input
        }], textTruncated: [{
            type: Output
        }], parentElement: [{
            type: Input
        }], scrollHandler: [{
            type: HostListener,
            args: ['window:resize']
        }], lineCount: [{
            type: Input
        }], text: [{
            type: Input
        }] }); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */
class NgxLineClampModule {
}
NgxLineClampModule.ɵmod = ɵngcc0.ɵɵdefineNgModule({ type: NgxLineClampModule });
NgxLineClampModule.ɵinj = ɵngcc0.ɵɵdefineInjector({ factory: function NgxLineClampModule_Factory(t) { return new (t || NgxLineClampModule)(); }, providers: [], imports: [[]] });
(function () { (typeof ngJitMode === "undefined" || ngJitMode) && ɵngcc0.ɵɵsetNgModuleScope(NgxLineClampModule, { declarations: [NgxLineClampDirective], exports: [NgxLineClampDirective] }); })();
/*@__PURE__*/ (function () { ɵngcc0.ɵsetClassMetadata(NgxLineClampModule, [{
        type: NgModule,
        args: [{
                imports: [],
                exports: [NgxLineClampDirective],
                declarations: [NgxLineClampDirective],
                providers: []
            }]
    }], null, null); })();

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

/**
 * @fileoverview added by tsickle
 * @suppress {checkTypes,extraRequire,missingOverride,missingReturn,unusedPrivateMembers,uselessCode} checked by tsc
 */

export { NgxLineClampModule, NgxLineClampDirective };

//# sourceMappingURL=ngx-line-clamp.js.map